import React, { useState, useRef, useEffect } from 'react';
import useIsMobile from '../hooks/useIsMobile';
import BottomSheet from './BottomSheet';

function sameVal(a, b) {
  return String(a) === String(b);
}

export default function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = 'اختر...',
  error,
  style,
  dataTestId,
  /** Show clear (×) when a non-empty value is selected — restores placeholder / empty option */
  allowClear = false,
  clearValue = '',
  multiple = false,
  /** When multiple: show 1. 2. order. Off = a set of destinations, not a path. */
  ordered = false,
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const isMobile = useIsMobile();
  const searchInputRef = useRef(null);
  const containerRef = useRef(null);

  const values = multiple
    ? (Array.isArray(value) ? value : (value != null && value !== '' ? [value] : []))
    : [];
  const selectedOption = options.find(o => o.value === value || o.value === Number(value))
    || options.find(o => String(o.value) === String(value));
  const hasValue = multiple
    ? values.length > 0
    : (value !== undefined && value !== null && String(value) !== '' && String(value) !== String(clearValue));
  const isOptionSelected = (opt) => (multiple
    ? values.some((v) => sameVal(v, opt.value))
    : sameVal(value, opt.value));
  const filteredOptions = search
    ? options.filter(o => (o.label || '').toLowerCase().includes(search.toLowerCase()))
    : options;

  function open() {
    if (disabled) return;
    setSearch('');
    setIsOpen(true);
  }

  function close() {
    setSearch('');
    setIsOpen(false);
  }

  function handleSelect(opt) {
    if (multiple) {
      const exists = values.some((v) => sameVal(v, opt.value));
      onChange(exists ? values.filter((v) => !sameVal(v, opt.value)) : [...values, opt.value]);
      setSearch('');
      return;
    }
    onChange(opt.value);
    close();
  }

  function handleClear(e) {
    e.preventDefault();
    e.stopPropagation();
    onChange(multiple ? [] : clearValue);
    close();
  }

  function removeValue(v, e) {
    e.preventDefault();
    e.stopPropagation();
    onChange(values.filter((x) => !sameVal(x, v)));
  }

  useEffect(() => {
    if (isMobile || !isOpen) return;
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        close();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isMobile, isOpen]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const triggerStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid ' + (error ? '#f44336' : '#ddd'),
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
    backgroundColor: 'white',
    textAlign: 'right',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: (multiple ? values.length : selectedOption) ? '#333' : '#999',
    outline: 'none',
    minHeight: '42px',
    flexWrap: multiple ? 'wrap' : 'nowrap',
    opacity: disabled ? 0.65 : 1,
    pointerEvents: disabled ? 'none' : 'auto',
    ...style,
  };

  const chipStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    maxWidth: '100%',
    padding: '3px 8px',
    borderRadius: 14,
    background: '#EDE7F6',
    color: '#3E2C6E',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.4,
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }} data-testid={dataTestId}>
      <div
        style={triggerStyle}
        onClick={open}
        tabIndex={0}
        role="combobox"
        aria-expanded={isOpen}
        data-testid={dataTestId ? `${dataTestId}-trigger` : undefined}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
      >
        <span style={{
          overflow: multiple ? 'visible' : 'hidden',
          textOverflow: multiple ? 'clip' : 'ellipsis',
          whiteSpace: multiple ? 'normal' : 'nowrap',
          flex: 1,
          display: multiple ? 'flex' : 'block',
          flexWrap: 'wrap',
          gap: 6,
          textAlign: 'right',
        }}>
          {multiple ? (
            values.length === 0
              ? placeholder
              : values.map((v, i) => {
                const opt = options.find((o) => sameVal(o.value, v));
                return (
                  <span key={`${v}-${i}`} style={chipStyle}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {ordered ? `${i + 1}. ${opt?.label || v}${i === 0 ? ' · وصول' : ''}` : (opt?.label || v)}
                    </span>
                    <button
                      type="button"
                      aria-label="إزالة"
                      onClick={(e) => removeValue(v, e)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: '#5c4d8a',
                        cursor: 'pointer',
                        fontSize: 14,
                        lineHeight: 1,
                        padding: 0,
                        fontWeight: 700,
                      }}
                    >
                      ×
                    </button>
                  </span>
                );
              })
          ) : (selectedOption ? selectedOption.label : placeholder)}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginRight: 4 }}>
          {allowClear && hasValue && (
            <button
              type="button"
              aria-label="مسح الاختيار"
              title="مسح — عرض الكل"
              onClick={handleClear}
              style={{
                border: 'none',
                background: '#f0edf5',
                color: '#5c4d8a',
                width: 22,
                height: 22,
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: 14,
                lineHeight: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                fontWeight: 700,
              }}
            >
              ×
            </button>
          )}
          <span style={{ fontSize: '10px', color: '#999' }}>▼</span>
        </span>
      </div>

      {isOpen && !isMobile && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          backgroundColor: 'white', border: '1px solid #ddd',
          borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          zIndex: 100, maxHeight: '300px', display: 'flex',
          flexDirection: 'column', overflow: 'hidden',
        }}>
          <div style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
            <input
              ref={searchInputRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="بحث..."
              dir="auto"
              data-testid={dataTestId ? `${dataTestId}-search` : undefined}
              style={{
                width: '100%', padding: '8px 10px', border: '1px solid #ddd',
                borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box',
                outline: 'none',
              }}
              onKeyDown={e => {
                e.stopPropagation();
                if (e.key === 'Escape') close();
                if (e.key === 'Enter' && filteredOptions.length === 1) {
                  handleSelect(filteredOptions[0]);
                }
              }}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filteredOptions.length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', color: '#999', fontSize: '13px' }}>لا توجد نتائج</div>
            ) : (
              filteredOptions.map((opt, i) => (
                <div
                  key={opt.value != null ? opt.value : i}
                  role="option"
                  data-testid={dataTestId ? `${dataTestId}-option` : undefined}
                  data-value={opt.value}
                  onClick={() => handleSelect(opt)}
                  style={{
                    padding: '10px 12px', cursor: 'pointer', fontSize: '14px',
                    backgroundColor: isOptionSelected(opt) ? '#EDE7F6' : 'transparent',
                    color: '#333', borderBottom: i < filteredOptions.length - 1 ? '1px solid #f5f5f5' : 'none',
                  }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = isOptionSelected(opt) ? '#EDE7F6' : 'transparent';
                  }}
                >
                  {multiple && isOptionSelected(opt)
                    ? (ordered
                      ? `✓ ${values.findIndex((v) => sameVal(v, opt.value)) + 1}. ${opt.label}`
                      : `✓ ${opt.label}`)
                    : opt.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {isOpen && isMobile && (
        <BottomSheet isOpen={isOpen} onClose={close}>
          <div style={{ padding: '0 0 var(--sp-md)' }}>
            <input
              ref={searchInputRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="بحث..."
              dir="auto"
              style={{
                width: '100%', padding: '12px', border: '1px solid #ddd',
                borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box',
                outline: 'none', marginBottom: '12px',
              }}
            />
            <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
              {filteredOptions.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '14px' }}>لا توجد نتائج</div>
              ) : (
                filteredOptions.map((opt, i) => (
                  <div
                    key={opt.value != null ? opt.value : i}
                    role="option"
                    data-testid={dataTestId ? `${dataTestId}-option` : undefined}
                    data-value={opt.value}
                    onClick={() => handleSelect(opt)}
                    style={{
                      padding: '14px 12px', cursor: 'pointer', fontSize: '16px',
                      backgroundColor: isOptionSelected(opt) ? '#EDE7F6' : 'transparent',
                      color: '#333', borderBottom: '1px solid #f0f0f0',
                      borderRadius: '8px',
                    }}
                  >
                    {multiple && isOptionSelected(opt)
                      ? (ordered
                        ? `✓ ${values.findIndex((v) => sameVal(v, opt.value)) + 1}. ${opt.label}`
                        : `✓ ${opt.label}`)
                      : opt.label}
                  </div>
                ))
              )}
            </div>
            {multiple && (
              <button
                type="button"
                onClick={close}
                style={{
                  width: '100%',
                  marginTop: 12,
                  padding: '12px',
                  border: 'none',
                  borderRadius: 8,
                  background: '#3E2C6E',
                  color: '#fff',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                تم
              </button>
            )}
          </div>
        </BottomSheet>
      )}
    </div>
  );
}
